import { PageShell } from "@superadmin/components/PageShell";
import { formatEpoch } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Paper } from "@voicely/ui-mui/layout/Paper";

/**
 * Read-only on purpose: flag writes live in `yarn feature-flag`, where a
 * change is a deliberate command with a diff in the shell history, not a
 * toggle a tired thumb can hit at 2am.
 */
export const FeatureFlagsPage = () => {
	const flagsQuery = useQuery(orpc.superAdmin.listFeatureFlags.queryOptions({ input: {} }));

	if (flagsQuery.isPending) {
		return (
			<PageShell title="Feature flags">
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (flagsQuery.error) {
		return (
			<PageShell title="Feature flags">
				<Alert severity="error">{flagsQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const flags = flagsQuery.data;

	return (
		<PageShell
			title="Feature flags"
			subtitle="Read-only — writes go through `yarn feature-flag` on the backend"
		>
			{flags.length === 0 ? (
				<Typography color="text.secondary">No flags set.</Typography>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Key</TableCell>
								<TableCell>Scope</TableCell>
								<TableCell>Enabled</TableCell>
								<TableCell>Notes</TableCell>
								<TableCell>Updated</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{flags.map((flag) => (
								<TableRow key={flag.id}>
									<TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{flag.key}</TableCell>
									<TableCell>
										{flag.scope === "global" ? (
											<Chip label="global" size="small" />
										) : (
											<Chip label={`team ${flag.scopeId}`} size="small" color="info" />
										)}
									</TableCell>
									<TableCell>
										<Chip
											label={flag.enabled ? "on" : "off"}
											size="small"
											color={flag.enabled ? "success" : "default"}
										/>
									</TableCell>
									<TableCell>{flag.notes ?? ""}</TableCell>
									<TableCell>{formatEpoch(flag.updatedAt)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</PageShell>
	);
};
